import axios, { AxiosResponse } from "axios";
import axiosInstance from "../../axiosInstance";
import {
  AnonTicketRequest,
  FollowUp,
  FollowUpAttachment,
  Ticket,
  TicketResponse,
  TicketsPage,
  TicketsPageResponse,
  UserTicket,
  UserTicketResponse,
} from "@/types/ticket";
import { FollowUpRequestForm } from "@/types/supportRequest";
import { prepareFileName } from "@/utils/fileHepler";

const statusToString = (status: number): string => {
  switch (status) {
    case 2:
      return "Reopened";
    case 3:
      return "Resolved";
    case 4:
      return "Closed";
    case 5:
      return "Duplicate";
    default:
      return "Open";
  }
};

const fetchTickets = async (
  page: number,
  page_size: number,
  hideClosed: boolean,
  ordering: string = "-created"
): Promise<TicketsPage> => {
  try {
    const response: AxiosResponse<TicketsPageResponse> =
      await axiosInstance.get("/api/helpdesk/api/tickets/", {
        params: {
          page,
          page_size,
          ordering,
          status: hideClosed ? "Open,Reopened" : undefined,
        },
      });

    const results = response.data.results?.map((ticket) => {
      return {
        ...ticket,
        status: statusToString(ticket.status),
        created: new Date(ticket.created),
        due_date: ticket.due_date ? new Date(ticket.due_date) : null,
      };
    });
    const ticketsPage = {
      ...response.data,
      results,
    };
    return ticketsPage;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
    } else {
      console.error("General error:", error);
    }
    throw error;
  }
};

const fetchUserTickets = async (
  is_checked?: boolean
): Promise<UserTicket[]> => {
  try {
    const response: AxiosResponse<UserTicketResponse[]> =
      await axiosInstance.get("/api/helpdesk/api/tickets/my-tickets", {
        params: {
          is_checked,
        },
      });

    return response.data?.map((ticket) => {
      return {
        ...ticket,
        status: statusToString(ticket.status),
        created: new Date(ticket.created),
        due_date: ticket.due_date ? new Date(ticket.due_date) : null,
      };
    });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
    } else {
      console.error("General error:", error);
    }
    throw error;
  }
};

const fetchTicket = async (id: string): Promise<Ticket> => {
  try {
    const response: AxiosResponse<TicketResponse> = await axiosInstance.get(
      `/api/helpdesk/api/tickets/${id}`
    );

    response.data.followups?.forEach((followUp) => {
      followUp.date = new Date(followUp.date);
    });
    return {
      ...response.data,
      status: statusToString(response.data.status),
      created: new Date(response.data.created),
      due_date: response.data.due_date
        ? new Date(response.data.due_date)
        : null,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
    } else {
      console.error("General error:", error);
    }
    throw error;
  }
};

const fetchFollowupAttachment = async (id: number) => {
  try {
    const response: AxiosResponse<FollowUpAttachment> = await axiosInstance.get(
      `/api/helpdesk/api/followups-attachment/${id}/`
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
    } else {
      console.error("General error:", error);
    }
    throw error;
  }
};

const markTicketAsChecked = async (id: string) => {
  try {
    await axiosInstance.put(`/api/helpdesk/api/tickets/${id}/mark-as-checked/`);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    } else {
      console.error("General error:", error);
      throw new Error("General error");
    }
  }
};

const createAnonTicket = async (
  ticket: AnonTicketRequest,
  recaptcha_token: string
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/api/helpdesk/anon/tickets/", {
      ...ticket,
      recaptcha_token,
    });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    } else {
      console.error("General error:", error);
      throw new Error("General error");
    }
  }
};

export interface CreateUserTicket {
  queue: number;
  title: string;
  description: string;
  resolution?: string;
  submitter_email?: string;
  assigned_to?: number;
  status: number;
  on_hold: boolean;
  priority: number;
  due_date: string;
  mergerd_to?: number;
  attachments: File[];
}

const createUserTicket = async (
  ticket: CreateUserTicket
): Promise<AxiosResponse> => {
  const formData = new FormData();
  for (const entry of Object.entries(ticket)) {
    if (entry[0] === "attachments") {
      for (const file of entry[1]) {
        formData.append(entry[0], prepareFileName(file));
      }
    } else {
      formData.append(entry[0], entry[1]);
    }
  }

  try {
    const response = await axiosInstance.post(
      "/api/helpdesk/create-ticket/",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw new Error(error.message);
    } else {
      console.error("General error:", error);
      throw new Error("General error");
    }
  }
};

const createFollowUp = async (
  ticket: string,
  userId: string,
  followUp: FollowUpRequestForm
): Promise<FollowUp> => {
  const formData = new FormData();
  for (const file of followUp.attachments) {
    formData.append("attachments", prepareFileName(file));
  }
  formData.append("comment", followUp.comment);
  formData.append("ticket", ticket);
  formData.append("user", userId);

  try {
    const response = await axiosInstance.post(
      "/api/helpdesk/api/followups/",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw new Error(error.message);
    } else {
      console.error("General error:", error);
      throw new Error("General error");
    }
  }
};

export {
  createFollowUp,
  createUserTicket,
  createAnonTicket,
  fetchTickets,
  fetchUserTickets,
  fetchTicket,
  fetchFollowupAttachment,
  markTicketAsChecked,
};
